import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Section, SectionHeading } from "@/components/Section";
import CharacterImage from "@/components/CharacterImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Characters() {
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/characters`)
      .then((r) => setCharacters(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Group characters by book_title
  const grouped = useMemo(() => {
    const groups = {};
    for (const c of characters) {
      const key = c.book_title || "Other";
      if (!groups[key]) groups[key] = [];
      groups[key].push(c);
    }
    return groups;
  }, [characters]);

  const bookOrder = Object.keys(grouped);

  return (
    <div className="page-wrapper pt-32 md:pt-40" data-testid="characters-page">
      <Section>
        <SectionHeading
          overline="Meet the Characters"
          title="The faces behind the stories."
          kicker="Cast portraits and visual teasers from across the catalog. Pinned, plotted, and lovingly obsessed over."
        />

        {loading ? (
          <div className="overline animate-pulse py-12">Loading…</div>
        ) : characters.length === 0 ? (
          <motion.div
            data-testid="characters-empty-state"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center py-24 md:py-32 border border-[#D4AF37]/5 rounded-2xl bg-[#2B3A4A]"
          >
            <div className="overline mb-6">Coming Soon</div>
            <h3 className="font-serif text-3xl md:text-4xl text-[#F4F4F6] font-light leading-tight mb-6 max-w-2xl mx-auto px-6">
              The cast is being polished.
            </h3>
            <p className="text-[#A0A4AE] text-base max-w-md mx-auto px-6 mb-10">
              Character portraits and teasers are on their way. Check back soon, or join The Letter to be the first to see them.
            </p>
            <Link to="/#newsletter" data-testid="characters-empty-cta" className="btn-primary">
              Join The Letter →
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-24">
            {bookOrder.map((bookTitle, bi) => (
              <motion.section
                key={bookTitle}
                data-testid={`characters-book-${bookTitle.toLowerCase().replace(/\s+/g, "-")}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: bi * 0.08 }}
              >
                <div className="flex items-baseline gap-5 mb-10">
                  <div className="overline whitespace-nowrap">From the Book</div>
                  <div className="h-px flex-1 bg-[#2B3A4A]/10" />
                  <Link
                    to={`/books/${grouped[bookTitle][0].book_slug}`}
                    className="font-serif text-2xl md:text-3xl text-[#F4F4F6] hover:text-[#D4AF37] transition-colors"
                  >
                    {bookTitle}
                  </Link>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                  {grouped[bookTitle].map((c, i) => (
                    <motion.article
                      key={c.id}
                      data-testid={`character-card-${c.slug}`}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-50px" }}
                      transition={{ duration: 0.6, delay: i * 0.06 }}
                      className="group flex flex-col"
                    >
                      <CharacterImage
                        src={c.image}
                        alt={c.name}
                        testId={`character-fallback-${c.slug}`}
                      />
                      <div className="pt-5 px-1">
                        <div className="overline mb-2 opacity-80">{bookTitle}</div>
                        <h3 className="font-serif text-2xl md:text-3xl text-[#F4F4F6] leading-tight group-hover:text-[#D4AF37] transition-colors">
                          {c.name}
                        </h3>
                      </div>
                    </motion.article>
                  ))}
                </div>
              </motion.section>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
